import { footprintRingsWorld, type SiteObject } from '@/site/SiteObject';
import { clear, el, formatLength } from './dom';
import { icon } from './Icons';
import type { SiteSummary } from './InspectorPanel';
import type { DisplayUnit } from './UiPreferences';
import { formatArea, type ManifestPlant, type PlantManifest } from './PlantManifest';
import {
  LENS_HEAT,
  lensHeatColor,
  type LensDefinition,
  type LensReading,
} from './FloraLens';

/**
 * The plan map: the manifest drawn where the plants actually are.
 *
 * A schedule answers "what and how many"; the only question it cannot answer is "where", and that is
 * the one a designer asks constantly — *which* of the eleven viburnum is the one crowding the path.
 * So the map is a second view of the same manifest rather than a separate feature: same species
 * colours, same tag numbers, same subset, and clicking a canopy selects the specimen in the scene.
 *
 * Drawn as SVG in **world metres**: the viewBox is the site extent, so a circle's radius is simply
 * the plant's canopy radius and no projection maths can drift from the model. Strokes are marked
 * non-scaling so hairlines stay hairlines at any card size.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

/** How the canopies are drawn — today's spread, or the spread these plants will grow into. */
export type CanopyMode = 'now' | 'mature';

export interface MapBounds {
  minX: number;
  minZ: number;
  width: number;
  length: number;
}

export interface PlantMapDeps {
  /** Element to mount into — the manifest overlay, so the map lives inside the HUD's frame. */
  host: HTMLElement;
  manifest: () => PlantManifest;
  site: () => SiteSummary | null;
  /** Buildings and paving, drawn as context so a plan is readable without the model. */
  structures: () => readonly SiteObject[];
  unit: () => DisplayUnit;
  isSelected: (instanceId: string) => boolean;
  /** Select in the scene. `additive` extends the current selection (shift-click). */
  onSelect: (instanceIds: readonly string[], additive: boolean) => void;
  /** Frame the camera on these plants — the map's double-click. */
  onFocus: (instanceIds: readonly string[]) => void;
  onClose?: () => void;
  /**
   * How each plant reads against the lens the map is being read through. Absent leaves the map
   * exactly the plan it has always been — the lens is a dress over it, not a second map.
   *
   * The lens is passed in rather than read off the bar here, because which lens is in force is a
   * property of *how the map was opened*: the plain plan from the Manifest menu stays plain no
   * matter what the design view is wearing. A map opened from the lens bar does follow it — see
   * {@link PlantMap.syncLens}.
   */
  reading?: (plant: ManifestPlant, lens: LensDefinition) => LensReading;
}

/**
 * World extent the plan should cover: the site, plus anything planted outside it, plus a margin.
 *
 * Plants beyond the site edge are *included* rather than clipped — a specimen dropped off the site
 * is a mistake the plan should reveal, not hide.
 */
export function mapBounds(
  plants: readonly { x: number; z: number; spreadMeters: number }[],
  site: SiteSummary | null,
): MapBounds {
  let minX = site ? -site.widthMeters / 2 : Number.POSITIVE_INFINITY;
  let maxX = site ? site.widthMeters / 2 : Number.NEGATIVE_INFINITY;
  let minZ = site ? -site.lengthMeters / 2 : Number.POSITIVE_INFINITY;
  let maxZ = site ? site.lengthMeters / 2 : Number.NEGATIVE_INFINITY;

  for (const plant of plants) {
    const radius = Math.max(0.25, plant.spreadMeters / 2);
    minX = Math.min(minX, plant.x - radius);
    maxX = Math.max(maxX, plant.x + radius);
    minZ = Math.min(minZ, plant.z - radius);
    maxZ = Math.max(maxZ, plant.z + radius);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minZ)) {
    return { minX: -10, minZ: -10, width: 20, length: 20 };
  }

  const margin = Math.max(1, Math.max(maxX - minX, maxZ - minZ) * 0.04);
  return {
    minX: minX - margin,
    minZ: minZ - margin,
    width: Math.max(1, maxX - minX + margin * 2),
    length: Math.max(1, maxZ - minZ + margin * 2),
  };
}

/**
 * A round number of metres for the scale bar — about a fifth of the plan, snapped to 1/2/5×10ⁿ so
 * the bar reads "10 m", never "13.7 m".
 */
export function niceScaleLength(spanMeters: number): number {
  const target = Math.max(0.5, spanMeters / 5);
  const magnitude = 10 ** Math.floor(Math.log10(target));
  for (const step of [1, 2, 5]) {
    if (step * magnitude >= target) return step * magnitude;
  }
  return 10 * magnitude;
}

/** Radius a plant is drawn at, in metres, under the chosen canopy mode. */
export function markerRadius(
  plant: ManifestPlant,
  matureSpread: number,
  mode: CanopyMode,
): number {
  const spread = mode === 'mature' ? Math.max(plant.spreadMeters, matureSpread) : plant.spreadMeters;
  // Groundcover and young perennials are sub-metre; without a floor they vanish on a 30 m plan.
  return Math.max(0.2, spread / 2);
}

export class PlantMap {
  readonly root: HTMLElement;

  private canopyMode: CanopyMode = 'now';
  private showTags = false;
  private showStructures = true;
  /** Species deliberately switched off in the legend. Empty means "everything". */
  private readonly muted = new Set<string>();

  /**
   * The lens the plan is read through, or null for the plain plan. A dress over the same map rather
   * than a second one: same bounds, same canopies, same click targets — only the colour changes, and
   * with it what the legend is a legend *of*.
   */
  private lens: LensDefinition | null = null;

  /**
   * Whether this map tracks the lens bar. True only when it was opened *from* the bar, which is the
   * one entry point that hands a lens in. The plain plan reached from the Manifest menu keeps being
   * the plain plan while a chip is picked behind it.
   */
  private follows = false;

  private readonly plot: HTMLElement;
  private readonly legend: HTMLElement;
  private readonly subtitle: HTMLElement;
  private readonly footNote: HTMLElement;
  private readonly title: HTMLElement;

  constructor(private readonly deps: PlantMapDeps) {
    this.plot = el('div', { class: 'lg-map__plot' });
    this.legend = el('div', { class: 'lg-map__legend' });
    this.subtitle = el('span', { class: 'lg-map__subtitle' });
    this.footNote = el('span', { class: 'lg-map__foot' });
    this.title = el('h2', { class: 'lg-map__title', text: 'Plan map' });

    this.root = el(
      'div',
      {
        class: 'lg-map',
        attrs: { role: 'dialog', 'aria-label': 'Plant plan map', hidden: true },
      },
      el(
        'header',
        { class: 'lg-map__head' },
        el('span', { class: 'lg-map__headicon', html: icon('top', 15) }),
        this.title,
        this.subtitle,
        el('div', { class: 'lg-map__tools' }, ...this.buildTools()),
        el('button', {
          class: 'lg-manifest__iconbutton',
          html: icon('close', 15),
          title: 'Close the map  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the plan map' },
          on: { click: () => this.close() },
        }),
      ),
      el('div', { class: 'lg-map__body' }, this.plot, this.legend),
      el('footer', { class: 'lg-map__footer' }, this.footNote),
    );

    // The editor listens for bare keys on `window`; Escape has to be claimed here or closing the
    // map would also clear the selection the map was just used to make.
    this.root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      event.preventDefault();
      this.close();
    });

    deps.host.appendChild(this.root);
  }

  get isOpen(): boolean {
    return this.root.hidden === false;
  }

  /**
   * Show the map. `speciesId` opens it on one species — the manifest's per-row map button — which
   * mutes the rest rather than dropping them, so the specimen is still read in context.
   *
   * `lens` opens it reading that lens; omitting it opens the plain plan, which is what the Manifest
   * menu has always done and still does.
   */
  open(speciesId?: string, lens?: LensDefinition | null): void {
    this.muted.clear();
    if (speciesId) {
      for (const entry of this.deps.manifest().species) {
        if (entry.definitionId !== speciesId) this.muted.add(entry.definitionId);
      }
    }
    // Being handed a lens argument at all — even an explicit null, for "the bar has no lens on" — is
    // what marks this as the bar's map, and so as one that should follow the bar from here.
    this.follows = lens !== undefined;
    // A lens with no way to read it is a lens that would draw every plant as unrecorded, so the map
    // falls back to the plain plan rather than to a grey one.
    this.lens = this.deps.reading ? lens ?? null : null;
    this.root.hidden = false;
    this.refresh();
    this.root.querySelector<HTMLElement>('button')?.focus();
  }

  close(): void {
    if (!this.isOpen) return;
    this.root.hidden = true;
    this.follows = false;
    this.deps.onClose?.();
  }

  /**
   * Re-dress an open map when the bar it was opened from moves — a different chip, the chip turned
   * off, or a switch between today and peak.
   *
   * The map and the bar are two views of one lens, so an open map going stale behind the bar reads
   * as the picker being broken. It redraws unconditionally rather than only on a change of lens id,
   * because the today/peak switch moves every reading without moving the lens.
   */
  syncLens(lens: LensDefinition | null): void {
    if (!this.isOpen || !this.follows) return;
    this.lens = this.deps.reading ? lens : null;
    this.refresh();
  }

  /** Redraw from the current manifest. Cheap enough to call on every editor change while open. */
  refresh(): void {
    if (!this.isOpen) return;
    const manifest = this.deps.manifest();
    const shown = manifest.plants.filter((plant) => !this.muted.has(plant.definitionId));
    const speciesShown = manifest.species.filter((entry) => !this.muted.has(entry.definitionId));

    this.root.classList.toggle('is-lensed', this.lens !== null);
    this.title.textContent = this.lens ? this.lens.label : 'Plan map';

    this.subtitle.textContent =
      shown.length === manifest.totalPlants
        ? `${manifest.totalPlants} plants · ${manifest.totalSpecies} species`
        : `${shown.length} of ${manifest.totalPlants} plants · ${speciesShown.length} of ${manifest.totalSpecies} species`;

    this.drawPlot(shown);
    this.drawLegend();

    const unit = this.deps.unit();
    const canopy =
      this.canopyMode === 'mature'
        ? manifest.matureCanopyAreaSquareMeters
        : manifest.canopyAreaSquareMeters;
    const site = this.deps.site();
    this.footNote.textContent = [
      site ? `${formatLength(site.widthMeters, unit)} × ${formatLength(site.lengthMeters, unit)} site` : null,
      `${formatArea(canopy, unit)} canopy ${this.canopyMode === 'mature' ? 'at maturity' : 'today'}`,
      'Click a canopy to select · shift-click to add · double-click to fly there',
    ]
      .filter((part): part is string => part !== null)
      .join('  ·  ');
  }

  /* ---------------------------------- The controls ---------------------------------- */

  private buildTools(): HTMLElement[] {
    const canopySegment = el('div', { class: 'lg-segment lg-segment--tight', attrs: { role: 'group', 'aria-label': 'Canopy size' } });
    for (const [mode, label, hint] of [
      ['now', 'Today', 'Draw each canopy at its current spread'],
      ['mature', 'Mature', 'Draw each canopy at its spread when grown — the overplanting check'],
    ] as const) {
      canopySegment.appendChild(
        el('button', {
          class: `lg-segment__item${this.canopyMode === mode ? ' is-active' : ''}`,
          text: label,
          title: hint,
          attrs: { type: 'button', 'data-canopy': mode },
          on: {
            click: () => {
              this.canopyMode = mode;
              for (const button of canopySegment.querySelectorAll('button')) {
                button.classList.toggle('is-active', button.dataset.canopy === mode);
              }
              this.refresh();
            },
          },
        }),
      );
    }

    return [
      canopySegment,
      this.toggleButton('Tags', 'Number every plant with its manifest tag', () => this.showTags, (on) => {
        this.showTags = on;
      }),
      this.toggleButton('Structures', 'Show buildings and paving for context', () => this.showStructures, (on) => {
        this.showStructures = on;
      }),
    ];
  }

  private toggleButton(
    label: string,
    title: string,
    get: () => boolean,
    set: (on: boolean) => void,
  ): HTMLElement {
    const button = el('button', {
      class: `lg-map__toggle${get() ? ' is-active' : ''}`,
      text: label,
      title,
      attrs: { type: 'button', 'aria-pressed': get() },
      on: {
        click: () => {
          set(!get());
          button.classList.toggle('is-active', get());
          button.setAttribute('aria-pressed', String(get()));
          this.refresh();
        },
      },
    });
    return button;
  }

  /* ----------------------------------- The drawing ----------------------------------- */

  private drawPlot(plants: readonly ManifestPlant[]): void {
    const manifest = this.deps.manifest();
    const site = this.deps.site();
    const bounds = mapBounds(manifest.plants, site);
    const span = Math.max(bounds.width, bounds.length);

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'lg-map__svg');
    svg.setAttribute(
      'viewBox',
      `${round(bounds.minX)} ${round(bounds.minZ)} ${round(bounds.width)} ${round(bounds.length)}`,
    );
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', `Plan of ${plants.length} plants`);

    if (this.lens) svg.appendChild(lensDefs(span));
    if (site) svg.appendChild(this.siteRect(site));
    if (this.showStructures) {
      for (const node of this.structureNodes()) svg.appendChild(node);
    }

    const matureSpread = new Map(
      manifest.species.map((entry) => [entry.definitionId, entry.matureSpreadMeters] as const),
    );

    // Big canopies first so a tree never hides the shrubs planted under it.
    const ordered = [...plants].sort(
      (a, b) =>
        markerRadius(b, matureSpread.get(b.definitionId) ?? 0, this.canopyMode) -
        markerRadius(a, matureSpread.get(a.definitionId) ?? 0, this.canopyMode),
    );

    // Under the canopies, blurred, and lit by screen blending: where several good plants stand
    // together their pools sum into a hotspot, which is the reading the plan is drawn for and the one
    // no per-plant symbol can give. Skipped entirely when there is no lens.
    if (this.lens) svg.appendChild(this.heatLayer(ordered, matureSpread));

    for (const plant of ordered) {
      svg.appendChild(
        this.plantNode(
          plant,
          this.plantColor(plant, manifest.colors.get(plant.definitionId) ?? '#3f8f45'),
          markerRadius(plant, matureSpread.get(plant.definitionId) ?? 0, this.canopyMode),
          span,
        ),
      );
    }

    if (this.showTags) {
      for (const plant of ordered) svg.appendChild(tagNode(plant, span));
    }

    for (const node of scaleBarNodes(bounds, this.deps.unit())) svg.appendChild(node);

    clear(this.plot);
    this.plot.appendChild(svg);
  }

  /* ------------------------------------ The lens dress ------------------------------------ */

  /** This plant's reading, or null when the map is drawing the plain plan. */
  private readingFor(plant: ManifestPlant): LensReading | null {
    if (!this.lens || !this.deps.reading) return null;
    return this.deps.reading(plant, this.lens);
  }

  /** Species colour on the plain plan; the heat ramp under a lens; grey where nothing is recorded. */
  private plantColor(plant: ManifestPlant, speciesColor: string): string {
    const reading = this.readingFor(plant);
    if (!reading) return speciesColor;
    return reading.described ? lensHeatColor(reading.value) : LENS_HEAT.unrecorded;
  }

  /**
   * The blurred pools under the canopies.
   *
   * Unrecorded plants contribute nothing here on purpose: a haze around a plant nobody has assessed
   * would read as a weak reading, and the hatched canopy above it is the honest way to say the
   * question has not been asked of that record.
   */
  private heatLayer(
    plants: readonly ManifestPlant[],
    matureSpread: ReadonlyMap<string, number>,
  ): SVGElement {
    const group = document.createElementNS(SVG_NS, 'g');
    group.setAttribute('class', 'lg-map__heat');
    group.setAttribute('filter', 'url(#lg-lens-blur)');

    for (const plant of plants) {
      const reading = this.readingFor(plant);
      if (!reading || !reading.described || reading.value <= 0) continue;
      const radius = markerRadius(plant, matureSpread.get(plant.definitionId) ?? 0, this.canopyMode);
      const pool = document.createElementNS(SVG_NS, 'circle');
      pool.setAttribute('cx', round(plant.x));
      pool.setAttribute('cy', round(plant.z));
      // Wider than the canopy so neighbours meet and their light adds, which is the whole point.
      pool.setAttribute('r', round(radius * 1.25));
      pool.setAttribute('fill', LENS_HEAT.hot);
      pool.setAttribute('opacity', round(0.18 + reading.value * 0.62));
      group.appendChild(pool);
    }

    return group;
  }

  private siteRect(site: SiteSummary): SVGElement {
    const rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('class', 'lg-map__site');
    rect.setAttribute('x', round(-site.widthMeters / 2));
    rect.setAttribute('y', round(-site.lengthMeters / 2));
    rect.setAttribute('width', round(site.widthMeters));
    rect.setAttribute('height', round(site.lengthMeters));
    return rect;
  }

  private structureNodes(): SVGElement[] {
    const nodes: SVGElement[] = [];
    for (const object of this.deps.structures()) {
      if (object.hidden) continue;
      for (const ring of footprintRingsWorld(object)) {
        if (ring.length < 3) continue;
        const polygon = document.createElementNS(SVG_NS, 'polygon');
        polygon.setAttribute('class', `lg-map__structure lg-map__structure--${object.kind}`);
        polygon.setAttribute('points', ring.map((point) => `${round(point.x)},${round(point.z)}`).join(' '));
        const title = document.createElementNS(SVG_NS, 'title');
        title.textContent = object.name;
        polygon.appendChild(title);
        nodes.push(polygon);
      }
    }
    return nodes;
  }

  private plantNode(
    plant: ManifestPlant,
    color: string,
    radius: number,
    span: number,
  ): SVGElement {
    const group = document.createElementNS(SVG_NS, 'g');
    const classes = ['lg-map__plant'];
    if (this.deps.isSelected(plant.id)) classes.push('is-selected');
    if (plant.hidden) classes.push('is-hidden');
    group.setAttribute('class', classes.join(' '));
    group.setAttribute('data-plant', plant.id);
    group.setAttribute('data-species', plant.definitionId);

    const reading = this.readingFor(plant);
    const unrecorded = reading !== null && !reading.described;

    const canopy = document.createElementNS(SVG_NS, 'circle');
    canopy.setAttribute(
      'class',
      unrecorded ? 'lg-map__canopy lg-map__canopy--unrecorded' : 'lg-map__canopy',
    );
    canopy.setAttribute('cx', round(plant.x));
    canopy.setAttribute('cy', round(plant.z));
    canopy.setAttribute('r', round(radius));
    // The hatch comes from the stylesheet for an unrecorded plant, so no fill is written over it.
    if (!unrecorded) canopy.setAttribute('fill', color);
    canopy.setAttribute('stroke', color);
    group.appendChild(canopy);

    // The centre dot is the plant's actual position — with mature canopies overlapping heavily it
    // is the only thing left that says how many plants a blob of colour is.
    const stem = document.createElementNS(SVG_NS, 'circle');
    stem.setAttribute('class', 'lg-map__stem');
    stem.setAttribute('cx', round(plant.x));
    stem.setAttribute('cy', round(plant.z));
    stem.setAttribute('r', round(Math.max(span / 400, 0.08)));
    stem.setAttribute('fill', color);
    group.appendChild(stem);

    const unit = this.deps.unit();
    const title = document.createElementNS(SVG_NS, 'title');
    title.textContent =
      `#${plant.index} ${plant.fullName} (${plant.commonName})\n` +
      `${formatLength(plant.heightMeters, unit)} H × ${formatLength(plant.spreadMeters, unit)} W · ` +
      `${Math.round(plant.maturity * 100)}% grown` +
      (plant.groupName ? `\nGroup: ${plant.groupName}` : '') +
      (this.lens && reading ? `\n${describeReading(this.lens, reading)}` : '');
    group.appendChild(title);

    group.addEventListener('click', (event) => {
      event.stopPropagation();
      this.deps.onSelect([plant.id], (event as MouseEvent).shiftKey);
      this.refresh();
    });
    group.addEventListener('dblclick', (event) => {
      event.stopPropagation();
      this.deps.onFocus([plant.id]);
    });

    return group;
  }

  private drawLegend(): void {
    const manifest = this.deps.manifest();
    clear(this.legend);

    // Under a lens the species list is the wrong legend: the colours on the plan no longer stand for
    // species at all. The ramp replaces it rather than joining it, so nothing on screen claims a
    // meaning the drawing does not have.
    if (this.lens) {
      this.drawRampLegend();
      return;
    }

    this.legend.appendChild(
      el(
        'div',
        { class: 'lg-map__legendhead' },
        el('span', { class: 'lg-map__legendtitle', text: 'Species' }),
        el('button', {
          class: 'lg-map__legendall',
          text: this.muted.size > 0 ? 'Show all' : 'Hide all',
          attrs: { type: 'button' },
          on: {
            click: () => {
              if (this.muted.size > 0) this.muted.clear();
              else for (const entry of manifest.species) this.muted.add(entry.definitionId);
              this.refresh();
            },
          },
        }),
      ),
    );

    for (const entry of manifest.species) {
      const muted = this.muted.has(entry.definitionId);
      this.legend.appendChild(
        el(
          'button',
          {
            class: `lg-map__key${muted ? ' is-muted' : ''}`,
            title: `${entry.fullName} — click to ${muted ? 'show' : 'hide'} on the plan`,
            attrs: { type: 'button', 'aria-pressed': !muted },
            on: {
              click: () => {
                if (muted) this.muted.delete(entry.definitionId);
                else this.muted.add(entry.definitionId);
                  this.refresh();
              },
            },
          },
          el('span', { class: 'lg-map__swatch', style: { background: entry.color } }),
          el(
            'span',
            { class: 'lg-map__keytext' },
            el('span', { class: 'lg-map__keyname', text: entry.fullName }),
            el('span', { class: 'lg-map__keycommon', text: entry.commonName }),
          ),
          el('span', { class: 'lg-map__keycount', text: String(entry.count) }),
        ),
      );
    }

    if (manifest.species.length === 0) {
      this.legend.appendChild(el('p', { class: 'lg-manifest__empty', text: 'Nothing planted yet.' }));
    }
  }

  /** The lens legend: a ramp, the question it answers, and an honest count of what is not known. */
  private drawRampLegend(): void {
    const lens = this.lens;
    if (!lens) return;

    const shown = this.deps
      .manifest()
      .plants.filter((plant) => !this.muted.has(plant.definitionId));
    const readings = shown.map((plant) => this.readingFor(plant));
    const unrecorded = readings.filter((reading) => reading !== null && !reading.described).length;
    const offering = readings.filter((reading) => reading !== null && reading.value > 0).length;

    this.legend.appendChild(
      el(
        'div',
        { class: 'lg-map__legendhead' },
        el('span', { class: 'lg-map__legendtitle', text: lens.label }),
      ),
    );

    this.legend.appendChild(el('p', { class: 'lg-map__rampnote', text: lens.question }));

    this.legend.appendChild(
      el(
        'div',
        { class: 'lg-map__ramp' },
        el('div', { class: 'lg-map__rampbar' }),
        el(
          'div',
          { class: 'lg-map__rampends' },
          el('span', { text: 'Offers little' }),
          el('span', { text: 'Offers most' }),
        ),
      ),
    );

    this.legend.appendChild(
      el('p', {
        class: 'lg-map__rampnote',
        text: `${offering} of ${shown.length} plants offer something here.`,
      }),
    );

    if (unrecorded > 0) {
      this.legend.appendChild(
        el(
          'div',
          { class: 'lg-map__rampkey' },
          el('span', { class: 'lg-map__rampswatch' }),
          el('span', {
            text: `${unrecorded} not recorded — the question has not been asked of these records, which is not the same as an answer of none.`,
          }),
        ),
      );
    }

    if (shown.length === 0) {
      this.legend.appendChild(el('p', { class: 'lg-manifest__empty', text: 'Nothing planted yet.' }));
    }
  }

  dispose(): void {
    this.root.remove();
  }
}

/** One line naming what a plant offers this lens, for its tooltip on the plan. */
function describeReading(lens: LensDefinition, reading: LensReading): string {
  if (!reading.described) return `${lens.label}: not recorded`;
  if (reading.contributions.length === 0) {
    return reading.peakValue > 0
      ? `${lens.label}: nothing on this date`
      : `${lens.label}: nothing`;
  }
  const parts = reading.contributions
    .slice(0, 3)
    .map((entry) => `${entry.label} ${Math.round(entry.value * 100)}%`);
  return `${lens.label}: ${parts.join(', ')}`;
}

/**
 * The blur and the hatch the lens layer needs.
 *
 * The blur radius is a fraction of the plan's own span rather than a fixed number, so a 300 m estate
 * and a 12 m courtyard get the same *looking* heat map instead of the estate getting a smudge and the
 * courtyard a set of hard discs.
 */
function lensDefs(span: number): SVGElement {
  const defs = document.createElementNS(SVG_NS, 'defs');

  const filter = document.createElementNS(SVG_NS, 'filter');
  filter.setAttribute('id', 'lg-lens-blur');
  // Generous bounds: the default -10%/120% box clips a blur this wide at the edges of the plan.
  filter.setAttribute('x', '-25%');
  filter.setAttribute('y', '-25%');
  filter.setAttribute('width', '150%');
  filter.setAttribute('height', '150%');
  const blur = document.createElementNS(SVG_NS, 'feGaussianBlur');
  blur.setAttribute('stdDeviation', round(Math.max(span / 90, 0.12)));
  filter.appendChild(blur);
  defs.appendChild(filter);

  const pattern = document.createElementNS(SVG_NS, 'pattern');
  pattern.setAttribute('id', 'lg-lens-hatch');
  pattern.setAttribute('patternUnits', 'userSpaceOnUse');
  const step = Math.max(span / 90, 0.12);
  pattern.setAttribute('width', round(step));
  pattern.setAttribute('height', round(step));
  pattern.setAttribute('patternTransform', 'rotate(45)');
  const stripe = document.createElementNS(SVG_NS, 'line');
  stripe.setAttribute('x1', '0');
  stripe.setAttribute('y1', '0');
  stripe.setAttribute('x2', '0');
  stripe.setAttribute('y2', round(step));
  stripe.setAttribute('stroke', LENS_HEAT.unrecorded);
  stripe.setAttribute('stroke-width', round(step / 3));
  pattern.appendChild(stripe);
  defs.appendChild(pattern);

  return defs;
}

/* ------------------------------------ SVG helpers ------------------------------------ */

function tagNode(plant: ManifestPlant, span: number): SVGElement {
  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('class', 'lg-map__tag');
  text.setAttribute('x', round(plant.x));
  text.setAttribute('y', round(plant.z));
  // Font size lives in world units so the tags scale with the plan rather than crowding when the
  // card is small — the same reason a paper plan's tag text is drawn at a plotted size.
  text.setAttribute('font-size', round(Math.max(span / 55, 0.3)));
  text.textContent = String(plant.index);
  return text;
}

/** Scale bar, bottom-left of the plan, in whichever unit the HUD is reading in. */
function scaleBarNodes(bounds: MapBounds, unit: DisplayUnit): SVGElement[] {
  const metersPerUnit = unit === 'feet' ? 0.3048 : 1;
  const barUnits = niceScaleLength(bounds.width / metersPerUnit);
  const barMeters = barUnits * metersPerUnit;
  const x = bounds.minX + bounds.width * 0.03;
  const y = bounds.minZ + bounds.length * 0.965;

  const line = document.createElementNS(SVG_NS, 'path');
  line.setAttribute('class', 'lg-map__scalebar');
  const tick = Math.max(bounds.length / 90, 0.15);
  line.setAttribute(
    'd',
    `M${round(x)} ${round(y - tick)} V${round(y)} H${round(x + barMeters)} V${round(y - tick)}`,
  );

  const label = document.createElementNS(SVG_NS, 'text');
  label.setAttribute('class', 'lg-map__scaletext');
  label.setAttribute('x', round(x));
  label.setAttribute('y', round(y - tick * 1.4));
  label.setAttribute('font-size', round(Math.max(bounds.width / 55, 0.3)));
  label.textContent = `${formatScaleNumber(barUnits)} ${unit === 'feet' ? 'ft' : 'm'}`;

  return [line, label];
}

function formatScaleNumber(value: number): string {
  return value < 1 ? value.toFixed(1) : String(Math.round(value));
}

function round(value: number): string {
  return (Math.round(value * 1000) / 1000).toString();
}
